import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { authManager } from '../utils/auth';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { UserContext } from '../contexts/UserContext';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

const Users_Edit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { setUser } = useContext(UserContext);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        role: '',
        userStatus: '',
        mfaEnabled: false
    });

    const [loading, setLoading] = useState(true);
    const [currentUserRole, setCurrentUserRole] = useState(null);
    
    // Check if current user is superadmin to conditionally show superadmin role option
    // Use backend validation instead of localStorage
    const isSuperAdmin = currentUserRole === 'superadmin';
    const roleOptions = isSuperAdmin ? ['admin', 'user', 'superadmin'] : ['admin', 'user'];
    const statusOptions = ['active', 'inactive'];

    useEffect(() => {
        const fetchUserAndValidateRole = async () => {
            try {
                // Validate current user role via backend
                const validatedRole = await authManager.validateUserRole();
                setCurrentUserRole(validatedRole);

                const response = await authManager.authenticatedFetch(
                    `${Base_Url}/users/${id}`
                );
                const data = await response.json();
                const user = data.result || {};

                setFormData({
                    name: user.name || '',
                    email: user.email || '',
                    role: user.role || '',
                    userStatus: user.status || '',
                    mfaEnabled: user.mfaEnabled || false
                });

                setLoading(false);
            } catch (error) {
                if (process.env.NODE_ENV === 'development') {
                    console.error('Error fetching user:', error);
                }
                toast.error('Failed to load user details');
                setLoading(false);
            }
        };

        fetchUserAndValidateRole();
    }, [id]);
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const { mfaEnabled, ...payload } = formData;

        try {
            const response = await authManager.authenticatedFetch(
                `${Base_Url}/users/${id}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                }
            );

            if (response.ok) {
                toast.success('User updated successfully');

                const loggedInUserId = localStorage.getItem('userId');
                if (id === loggedInUserId) {
                    localStorage.setItem('name', formData.name);
                    setUser(prev => ({ ...prev, name: formData.name }));
                }

                setTimeout(() => navigate('/users'), 2000);
            } else {
                const errData = await response.json();
                const errMsg = errData?.error?.errors || errData?.error?.message || 'Update failed';
                toast.error(errMsg);
            }
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.error('Update error:', error);
            }
            toast.error('Something went wrong');
        } finally {
            setIsSubmitting(false);
        }
    };
    return (
        <div>
            <ToastContainer position="top-right" autoClose={1500} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2 align-items-center">
                        <div className="col-6 col-sm-6">
                            <h1 className="m-0 txt_lft">Edit User</h1>
                        </div>
                        <div className="col-6 col-sm-6">
                            <ol className="breadcrumb justify-content-end">
                                <li className="breadcrumb-item">
                                    <Link to="/users"><i className="fa-solid fa-arrow-left-long" /> Back</Link>
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="frmwdth_cntr">
                            <div className="col-lg-12">
                                <div className="card card-primary card-outline">
                                    {loading ? (
                                        <div className="card-body text-center">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <form className="form-horizontal" onSubmit={handleSubmit}>
                                            <div className="card-body">
                                                <div className="form-group row align-items-center">
                                                    <label className="col-12 col-md-4 col-form-label">Name</label>
                                                    <div className="col-12 col-md-8">
                                                        <input name="name" type="text" className="form-control" value={formData.name} onChange={handleChange} />
                                                    </div>
                                                </div>

                                                <div className="form-group row align-items-center">
                                                    <label className="col-12 col-md-4 col-form-label">Email</label>
                                                    <div className="col-12 col-md-8">
                                                        {formData.email}
                                                    </div>
                                                </div>

                                                <div className="form-group row align-items-center">
                                                    <label className="col-12 col-md-4 col-form-label">2FA</label>
                                                    <div className="col-12 col-md-8">
                                                        {formData.mfaEnabled ? (
                                                            <span className="f_green fwt_600">Enabled</span>
                                                        ) : (
                                                            <span className="f_red fwt_600">Disabled</span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="form-group row">
                                                    <label className="col-md-4 col-form-label">Role</label>
                                                    <div className="col-md-8">
                                                        <select name="role" className="form-control" value={formData.role} onChange={handleChange}>
                                                            <option value="">Select Role</option>
                                                            {roleOptions.map(roleValue => (
                                                                <option key={roleValue} value={roleValue}>
                                                                    {roleValue === 'superadmin' ? 'Super Admin' : 
                                                                     roleValue === 'admin' ? 'Admin' : 
                                                                     roleValue === 'user' ? 'User' : roleValue}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="form-group row">
                                                    <label className="col-md-4 col-form-label">Status</label>
                                                    <div className="col-md-8">
                                                        <select
                                                            name="userStatus"
                                                            className="form-control"
                                                            value={formData.userStatus}
                                                            onChange={handleChange}
                                                        >
                                                            <option value="">Select Status</option>
                                                            {statusOptions.map(status => (
                                                                <option key={status} value={status}>
                                                                    {status}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>

                                            </div>

                                            <div className="card-footer text-center" >
                                                <button type="submit" className="btn btn-blue" disabled={isSubmitting}>
                                                    {isSubmitting ? 'Updating...' : 'Update'}
                                                </button>

                                            </div>
                                        </form>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Users_Edit;
